import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { motion } from "framer-motion";
import { Copy, Check, Users, LayoutDashboard, Share2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { getSession, updatePaymentInfo } from "@/lib/api";

export default function ShareSession() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revolut, setRevolut] = useState(null);
  const [savingRev, setSavingRev] = useState(false);

  const joinUrl = `${window.location.origin}/join/${code}`;

  useEffect(() => {
    const load = () =>
      getSession(code)
        .then((data) => {
          setS(data);
          setRevolut((prev) => (prev === null ? data.revolut_link || "" : prev));
        })
        .catch(() => {});
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [code]);

  const saveRevolut = async () => {
    setSavingRev(true);
    try {
      const data = await updatePaymentInfo(code, (revolut || "").trim());
      setS(data);
      toast.success("Link Revolut salvat");
    } catch {
      toast.error("Nu am putut salva linkul");
    } finally {
      setSavingRev(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(joinUrl);
    setCopied(true);
    toast.success("Link copiat");
    setTimeout(() => setCopied(false), 1800);
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "SplitBon", text: "Alătură-te ca să decontăm nota:", url: joinUrl });
      } catch {}
    } else {
      copy();
    }
  };

  const guests = s?.guests || [];

  return (
    <div className="min-h-screen bg-background pb-28">
      <Header subtitle="Invită prietenii" />
      <main className="max-w-md mx-auto px-4 sm:px-6 pt-8">
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Pasul 4</span>
        <h1 className="font-display text-3xl font-black tracking-tight mt-2">Invită-i la masă</h1>
        <p className="text-muted-foreground mt-2">Scanează codul QR sau trimite linkul. Vor alege ce au consumat.</p>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mt-6 bg-card border border-border rounded-[28px] p-7 flex flex-col items-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
        >
          <div className="bg-white p-4 rounded-2xl" data-testid="qr-code">
            <QRCodeCanvas value={joinUrl} size={200} level="M" />
          </div>
          <div className="mt-5 text-center">
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Cod sesiune</span>
            <p data-testid="session-code" className="font-mono text-2xl font-bold tracking-[0.2em] mt-1">{code}</p>
          </div>
        </motion.div>

        <div className="mt-4 flex gap-3">
          <button
            data-testid="copy-link-button"
            onClick={copy}
            className="flex-1 bg-secondary rounded-full py-4 font-semibold active:scale-[0.98] transition flex items-center justify-center gap-2"
          >
            {copied ? <Check size={18} className="text-success" /> : <Copy size={18} />} {copied ? "Copiat" : "Copiază link"}
          </button>
          <button
            data-testid="share-link-button"
            onClick={share}
            className="flex-1 bg-secondary rounded-full py-4 font-semibold active:scale-[0.98] transition flex items-center justify-center gap-2"
          >
            <Share2 size={18} /> Distribuie
          </button>
        </div>

        <div className="mt-6 bg-card border border-border rounded-[24px] p-5">
          <div className="flex items-center gap-2 text-muted-foreground">
            <CreditCard size={16} />
            <span className="text-xs uppercase tracking-[0.15em] font-bold">Linkul tău Revolut</span>
          </div>
          <div className="flex gap-2 mt-3">
            <input
              data-testid="share-revolut-input"
              value={revolut ?? ""}
              onChange={(e) => setRevolut(e.target.value)}
              placeholder="ex: revolut.me/andrei"
              className="flex-1 bg-secondary rounded-2xl px-4 py-3 outline-none focus:ring-2 ring-ring"
            />
            <button
              data-testid="save-revolut-button"
              onClick={saveRevolut}
              disabled={savingRev}
              className="bg-primary text-primary-foreground rounded-2xl px-5 font-semibold active:scale-95 transition disabled:opacity-60"
            >
              {savingRev ? "..." : "Salvează"}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Prietenii care aleg „Revolut" vor deschide direct linkul, cu suma completată automat.
          </p>
        </div>

        <div className="mt-6 bg-card border border-border rounded-[24px] p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users size={16} />
              <span className="text-xs uppercase tracking-[0.15em] font-bold">Au intrat</span>
            </div>
            <span data-testid="guest-count" className="font-mono font-bold">{guests.length}</span>
          </div>
          {guests.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-3">Încă nu s-a alăturat nimeni...</p>
          ) : (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="joined-guests">
              {guests.map((g) => (
                <motion.span
                  key={g.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-2 bg-secondary rounded-full pl-1 pr-3 py-1"
                >
                  <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                    {g.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="text-sm font-medium">{g.name}</span>
                </motion.span>
              ))}
            </div>
          )}
        </div>
      </main>

      <div className="fixed bottom-0 left-0 right-0 glass bg-background/80 border-t border-border p-4">
        <div className="max-w-md mx-auto">
          <button
            data-testid="go-to-dashboard-button"
            onClick={() => navigate(`/session/${code}/dashboard`)}
            className="w-full bg-primary text-primary-foreground rounded-full py-4 px-6 text-lg font-bold active:scale-[0.98] transition-transform flex justify-center items-center gap-2"
          >
            <LayoutDashboard size={20} /> Vezi panoul de control
          </button>
        </div>
      </div>
    </div>
  );
}
